import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2, Crown, Users, Clock, X, MapPin } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { usePayment } from '@/lib/payment';
import { formatINR, formatDate, pad } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useVenue } from '@/lib/venue';

export default function Memberships() {
  const [plans, setPlans] = useState([]);
  const [mine, setMine] = useState([]);
  const [courts, setCourts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [slotPlan, setSlotPlan] = useState(null);
  const [offers, setOffers] = useState([]);
  const [sports, setSports] = useState([]);
  const { toast } = useToast();
  const pay = usePayment();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Plans/courts are venue-scoped — see lib/venue.jsx. The chosen venue
  // persists across navigation (see BookCourt.jsx) instead of being asked
  // again on every visit.
  const {
    selectedVenueId,
    needsPicker,
    loading: venuesLoading,
  } = useVenue();

  useEffect(() => {

  if (
    user &&
    !user.phone
  ) {

    navigate('/app/complete-profile', {
      state: {
        redirectTo: '/app/memberships',
      },
    });
  }

}, [user, navigate]);

  useEffect(() => {
    if (needsPicker) {
      navigate('/app/venues', { state: { redirectTo: '/app/memberships' } });
    }
  }, [needsPicker, navigate]);

  const load = async () => {
    const params = selectedVenueId ? { academy_id: selectedVenueId } : {};
    const [p, m, c] = await Promise.all([
      api.get('/plans', { params }),
      api.get('/memberships/mine'),
      api.get('/courts', { params }),
    ]);
    setPlans(p.data);
    setMine(m.data);
    setCourts(c.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!venuesLoading && !needsPicker) load(); }, [selectedVenueId, venuesLoading, needsPicker]);
  useEffect(() => { api.get('/sports').then((r) => setSports(r.data)).catch(() => {}); }, []);
  const sportName = (id) => sports.find((s) => s.id === id)?.name;

  // Discounts & offers: purely informational — the actual purchase price
  // always comes back from the membership the backend creates (see
  // app.services.best_discount), this just previews what applies.
  useEffect(() => {
    if (venuesLoading || needsPicker) return;
    api.get('/offers', { params: selectedVenueId ? { academy_id: selectedVenueId } : {} })
      .then((r) => setOffers((r.data || []).filter((o) => o.applies_to !== 'booking')))
      .catch(() => setOffers([]));
  }, [selectedVenueId, venuesLoading, needsPicker]);

  const bestOfferFor = (price) => {
    if (offers.length === 0 || !price) return null;
    let best = null;
    for (const o of offers) {
      const amt = o.discount_type === 'percent' ? (price * o.discount_value) / 100 : Math.min(o.discount_value, price);
      if (!best || amt > best.amt) best = { ...o, amt };
    }
    return best;
  };

  const handleJoin = (plan) => {
    if (plan.allows_slot_reservation) {
      setSlotPlan(plan);
    } else {
      purchase(plan, {});
    }
  };

  const purchase = async (plan, extra) => {
    setBusy(plan.id);
    try {
      const { data } = await api.post('/memberships/purchase', { plan_id: plan.id, ...extra });
      setSlotPlan(null);
      pay({
        purpose: 'membership',
        reference_id: data.id,
        onSuccess: () => { toast({ title: 'Membership active!', variant: 'success' }); load(); },
      });
    } catch (e) {
      toast({ title: 'Purchase failed', description: e.response?.data?.detail || '', variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const active = mine.find((m) => m.status === 'active' && new Date(m.expires_at) > new Date());

  return (
    <div className="space-y-12" data-testid="memberships-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Plan & Perks</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight">Memberships</h1>
      </div>

      {active && (
        <div className="bg-volt/10 border border-volt p-6 sm:p-8 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          <div>
            <Badge tone="volt" className="mb-2"><Crown size={10} className="mr-1" /> Active Membership</Badge>
            <h2 className="font-heading font-black text-3xl uppercase tracking-tight">{active.plan_name}</h2>
            <p className="text-sm text-white/70 mt-2">
              <span className="font-mono text-volt">{active.member_code}</span> · Valid until {formatDate(active.expires_at)} · {active.discount_percent}% off all bookings
            </p>
            {active.is_team && (
              <p className="text-xs text-white/60 mt-1"><Users size={11} className="inline mr-1" /> Team plan · up to {active.team_size} players per court</p>
            )}
            {active.reserved_court_name && active.reserved_start_hour != null && (
              <p className="text-xs text-volt mt-1">
                <Clock size={11} className="inline mr-1" /> Daily reserved: {active.reserved_court_name} · {pad(active.reserved_start_hour)}:00 – {pad(active.reserved_start_hour + active.reserved_duration_hours)}:00
              </p>
            )}
          </div>
          <CheckCircle2 size={48} className="text-volt" />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((p) => {
          const offer = bestOfferFor(p.price);
          return (
          <div
            key={p.id}
            className={`relative p-8 bg-surface border ${p.highlight ? 'border-volt volt-glow' : 'border-white/10'} flex flex-col`}
            data-testid={`plan-card-${p.name.toLowerCase()}`}
          >
            {p.highlight && (
              <span className="absolute -top-3 left-8 bg-volt text-black text-[10px] uppercase tracking-[0.25em] font-black px-3 py-1">Most Popular</span>
            )}
            {p.is_team && (
              <span className="absolute -top-3 right-8 bg-white text-black text-[10px] uppercase tracking-[0.25em] font-black px-3 py-1 flex items-center gap-1">
                <Users size={10} /> Team
              </span>
            )}
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2 flex items-center gap-2 flex-wrap">
              <span>{p.duration_days} day pass</span>
              {sportName(p.sport_id) && <Badge tone="volt">{sportName(p.sport_id)}</Badge>}
            </div>
            <h3 className="font-heading font-black text-3xl uppercase mb-2">{p.name}</h3>
            <p className="text-sm text-white/50 mb-5 min-h-[40px]">{p.description}</p>
            <div className="mb-4">
              {offer ? (
                <>
                  <span className="text-white/40 line-through text-lg mr-2">{formatINR(p.price)}</span>
                  <span className="font-heading font-black text-5xl text-volt">{formatINR(p.price - offer.amt)}</span>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-volt mt-1">🎉 {offer.title}</div>
                </>
              ) : (
                <span className="font-heading font-black text-5xl">{formatINR(p.price)}</span>
              )}
            </div>
            {p.is_team && (
              <div className="mb-4 px-3 py-2 bg-white/5 border border-white/10 text-xs text-white/70 flex items-center gap-2">
                <Users size={12} className="text-volt" /> Up to {p.team_size} players/court
              </div>
            )}
            {p.allows_slot_reservation && (
              <div className="mb-4 px-3 py-2 bg-volt/10 border border-volt/40 text-xs text-volt flex items-center gap-2">
                <Clock size={12} /> Pick a daily reserved slot at checkout
              </div>
            )}
            <ul className="space-y-3 mb-8 flex-1">
              {p.perks.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm text-white/70">
                  <span className="text-volt mt-1">▸</span>
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
            <Button
              variant={p.highlight ? 'default' : 'outline'}
              className="w-full"
              onClick={() => handleJoin(p)}
              disabled={busy === p.id}
              data-testid={`purchase-${p.name.toLowerCase()}`}
            >
              {busy === p.id ? <><Loader2 size={14} className="animate-spin" /> Processing</> : <>Join {p.name} <ArrowRight size={14} /></>}
            </Button>
          </div>
          );
        })}
      </div>

      {mine.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-2xl uppercase tracking-tight mb-4">Membership History</h2>
          <div className="bg-surface border border-white/10 overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="border-b border-white/10">
                <tr className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                  <th className="text-left px-5 py-3 font-bold">Member ID</th>
                  <th className="text-left px-5 py-3 font-bold">Plan</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-left px-5 py-3 font-bold">Reserved Slot</th>
                  <th className="text-left px-5 py-3 font-bold">Expires</th>
                  <th className="text-right px-5 py-3 font-bold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {mine.map((m) => (
                  <tr key={m.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-5 py-3 font-mono text-volt">{m.member_code || '—'}</td>
                    <td className="px-5 py-3 font-medium">{m.plan_name}{m.is_team ? <span className="ml-2"><Badge tone="default">Team · {m.team_size}</Badge></span> : null}</td>
                    <td className="px-5 py-3"><Badge tone={m.status === 'active' ? 'success' : 'default'}>{m.status}</Badge></td>
                    <td className="px-5 py-3 text-white/70 text-xs">
                      {m.reserved_court_name && m.reserved_start_hour != null
                        ? `${m.reserved_court_name} · ${pad(m.reserved_start_hour)}:00 – ${pad(m.reserved_start_hour + m.reserved_duration_hours)}:00`
                        : '—'}
                    </td>
                    <td className="px-5 py-3 text-white/60">{m.expires_at ? formatDate(m.expires_at) : '—'}</td>
                    <td className="px-5 py-3 text-right font-mono">{formatINR(m.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {slotPlan && (
        <SlotPickerModal
          plan={slotPlan}
          courts={slotPlan.sport_id ? courts.filter((c) => c.sport_id === slotPlan.sport_id) : courts}
          onClose={() => setSlotPlan(null)}
          onConfirm={(payload) => purchase(slotPlan, payload)}
          busy={busy === slotPlan.id}
        />
      )}
    </div>
  );
}

function SlotPickerModal({ plan, courts, onClose, onConfirm, busy }) {
  const [courtId, setCourtId] = useState(courts[0]?.id || '');
  const court = useMemo(() => courts.find((c) => c.id === courtId), [courts, courtId]);
  const [startHour, setStartHour] = useState(court ? court.open_hour : 6);
  const [duration, setDuration] = useState(1);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!court) return;
    if (startHour < court.open_hour) setStartHour(court.open_hour);
    if (startHour + duration > court.close_hour) setStartHour(court.close_hour - duration);
  }, [courtId]); // eslint-disable-line

  // Fetch availability for today to surface conflicting slots
  useEffect(() => {
    if (!court) return;
    const today = new Date().toISOString().slice(0, 10);
    setLoading(true);
    api.get(`/courts/${court.id}/availability`, { params: { date: today } })
      .then((r) => setConflicts(r.data.slots.filter((s) => s.reason === 'member_reserved').map((s) => s.hour)))
      .finally(() => setLoading(false));
  }, [court]);

  const submit = () => {
    if (!court) return toast({ title: 'Pick a court', variant: 'error' });
    if (startHour < court.open_hour || startHour + duration > court.close_hour) {
      return toast({ title: 'Out of operating hours', description: `${court.name} is open ${pad(court.open_hour)}:00 – ${pad(court.close_hour)}:00`, variant: 'error' });
    }
    // local conflict check
    for (let h = startHour; h < startHour + duration; h++) {
      if (conflicts.includes(h)) {
        return toast({ title: 'Slot reserved by another member', description: 'Pick a different time or court', variant: 'error' });
      }
    }
    onConfirm({ reserved_court_id: courtId, reserved_start_hour: startHour, reserved_duration_hours: duration });
  };

  if (!court) {
    return (
      <ModalShell onClose={onClose} title={`Lock your daily slot — ${plan.name}`}>
        <p className="text-sm text-white/60">No active courts available. Please contact the venue.</p>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} title={`Lock your daily slot — ${plan.name}`}>
      <p className="text-sm text-white/60 mb-6">This slot will be reserved <span className="text-volt">every day</span> for the next {plan.duration_days} days. Other members cannot book it.</p>
      <div className="space-y-5">
        <Field label="Court">
          <select
            value={courtId}
            onChange={(e) => setCourtId(e.target.value)}
            className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none"
            data-testid="reservation-court-select"
          >
            {courts.map((c) => (
              <option key={c.id} value={c.id} className="bg-obsidian">{c.name} · {pad(c.open_hour)}:00 – {pad(c.close_hour)}:00</option>
            ))}
          </select>
        </Field>
        <Field label={`Start hour (${pad(court.open_hour)}:00 – ${pad(court.close_hour - duration)}:00)`}>
          <select
            value={startHour}
            onChange={(e) => setStartHour(Number(e.target.value))}
            className="w-full h-11 px-4 bg-transparent border border-white/20 text-white focus:border-volt focus:outline-none"
            data-testid="reservation-start-hour"
          >
            {Array.from({ length: court.close_hour - court.open_hour }).map((_, i) => {
              const h = court.open_hour + i;
              const isConflict = Array.from({ length: duration }).some((__, di) => conflicts.includes(h + di));
              const exceedsClose = h + duration > court.close_hour;
              const disabled = isConflict || exceedsClose;
              return (
                <option key={h} value={h} disabled={disabled} className="bg-obsidian">
                  {pad(h)}:00 {disabled ? (exceedsClose ? '(exceeds close)' : '(reserved)') : ''}
                </option>
              );
            })}
          </select>
        </Field>
        <Field label="Duration">
          <div className="flex gap-2">
            {[1, 2].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDuration(d)}
                className={`px-5 h-11 text-xs font-bold uppercase tracking-wider border ${duration === d ? 'border-volt text-volt bg-volt/5' : 'border-white/15 text-white/60 hover:border-white/30'}`}
                data-testid={`reservation-duration-${d}`}
              >{d} hour{d > 1 ? 's' : ''}</button>
            ))}
          </div>
        </Field>
        <div className="bg-obsidian border border-white/10 p-4 text-xs">
          <div className="text-white/40 uppercase tracking-[0.25em] mb-2">Your reservation</div>
          <div className="text-white">
            <MapPin size={12} className="inline mr-1 text-volt" /> {court.name}
          </div>
          <div className="text-white mt-1">
            <Clock size={12} className="inline mr-1 text-volt" /> Daily · {pad(startHour)}:00 – {pad(startHour + duration)}:00 · for {plan.duration_days} days
          </div>
        </div>
      </div>
      <div className="flex gap-3 mt-8">
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button onClick={submit} disabled={busy || loading} className="flex-1" data-testid="confirm-reservation">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Processing</> : <>Continue to Pay <ArrowRight size={14} /></>}
        </Button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface border border-white/10 max-w-lg w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6 gap-4">
          <h2 className="font-heading font-bold text-2xl uppercase tracking-tight">{title}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
